import React, { useState, useEffect } from 'react';
import { supabase } from './lib/supabase';
import Layout from './components/Layout';
import LoginForm from './components/Auth/LoginForm';
import CandidateProfile from './components/Candidate/CandidateProfile';
import EvaluationList from './components/Evaluator/EvaluationList';
import EvaluatorProfile from './components/Evaluator/EvaluatorProfile';
import AdminDashboard from './components/Admin/AdminDashboard';
import PublicDiscussion from './components/PublicDiscussion';

type UserType = 'candidate' | 'evaluator' | null;
type CurrentPage = 'candidates' | 'evaluator' | 'evaluations' | 'admin' | 'discussion';

function App() {
  const [userType, setUserType] = useState<UserType>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentPage, setCurrentPage] = useState<CurrentPage>('evaluations');
  const [evalListKey, setEvalListKey] = useState(0); // for forcing EvaluationList to reset to list view
  const [loading, setLoading] = useState(true);
  const [pendingGoAdmin, setPendingGoAdmin] = useState(false);
  const [pendingGoNormal, setPendingGoNormal] = useState<CurrentPage | null>(null);

  useEffect(() => {
    checkUser();
    try {
      const goto = localStorage.getItem('goto_admin');
      if (goto) {
        setPendingGoAdmin(true);
        localStorage.removeItem('goto_admin');
      }
      const back = localStorage.getItem('goto_normal');
      if (back) {
        setPendingGoNormal(back as CurrentPage);
        localStorage.removeItem('goto_normal');
      }
    } catch {}
  }, []);

  // After user info is loaded, honor pending navigation intent
  useEffect(() => {
    if (!loading) {
      if (pendingGoAdmin) {
        if (isAdmin) {
          setCurrentPage('admin');
        }
        setPendingGoAdmin(false);
      } else if (pendingGoNormal) {
        setCurrentPage(pendingGoNormal);
        setPendingGoNormal(null);
      }
    }
  }, [loading, isAdmin, pendingGoAdmin, pendingGoNormal]);

  const checkUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        await determineUserType(session.user.email!);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    } finally {
      setLoading(false);
    }
  };

  const determineUserType = async (email: string) => {
    try {
      // Check if admin
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: adminData } = await supabase
          .from('admin_users')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();
        setIsAdmin(!!adminData);
      }

      // Check if candidate
      const { data: candidateData } = await supabase
        .from('candidates')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (candidateData) {
        setUserType('candidate');
        setCurrentPage('candidates');
        return;
      }

      // Check if evaluator
      const { data: evaluatorData } = await supabase
        .from('evaluators')
        .select('*')
        .eq('email', email)
        .maybeSingle();

      if (evaluatorData) {
        setUserType('evaluator');
        setCurrentPage('evaluations');
        return;
      }

      // If no user type found, logout
      await supabase.auth.signOut();
      setUserType(null);
    } catch (error) {
      console.error('Error determining user type:', error);
      setUserType(null);
    }
  };

  const handleLogin = async (type: UserType | 'admin') => {
    setUserType(type);
    if (type === 'candidate') {
      setCurrentPage('candidates');
    } else {
      setCurrentPage('evaluations');
    }
  };

  const handleLogout = () => {
    setUserType(null);
    setCurrentPage('evaluations');
  };

  const renderCurrentPage = () => {
    if (!userType) return null;

    switch (currentPage) {
      case 'candidates':
        return userType === 'candidate' ? <CandidateProfile /> : <EvaluationList />;
      case 'evaluator':
        return userType === 'evaluator' ? <EvaluatorProfile /> : <EvaluationList />;
      case 'evaluations':
        return <EvaluationList />;
      case 'admin':
        return isAdmin ? <AdminDashboard /> : <EvaluationList />;
      case 'discussion':
        return <PublicDiscussion />;
      default:
        return <EvaluationList />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!userType) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const handlePageChange = (page: CurrentPage) => {
    if (page === 'evaluations') {
      // Even if already on evaluations, bump key to reset list view
      setEvalListKey((k) => k + 1);
    }
    setCurrentPage(page);
  };

  return (
    <Layout
      currentPage={currentPage}
      onPageChange={handlePageChange}
      userType={userType}
      isAdmin={isAdmin}
      onLogout={handleLogout}
    >
      {(() => {
        if (!userType) return null;
        switch (currentPage) {
          case 'candidates':
            // Always show a profile on My Page routes; fall back to the other profile instead of list
            return userType === 'candidate' ? <CandidateProfile /> : <EvaluatorProfile />;
          case 'evaluator':
            return userType === 'evaluator' ? <EvaluatorProfile /> : <CandidateProfile />;
          case 'evaluations':
            return <EvaluationList navKey={evalListKey} />;
          case 'admin':
            return isAdmin ? <AdminDashboard /> : <EvaluationList />;
          case 'discussion':
            return <PublicDiscussion />;
          default:
            return <EvaluationList />;
        }
      })()}
    </Layout>
  );
}

export default App;
